/**
 * 資料エディタ画面 (Phase 8)。
 * ランディングエディタと同等の 3 カラム構成:
 *   - ヘッダー: 戻る / タイトル / ビューポート切替 / 公開ページリンク / 公開ボタン
 *   - 左: BriefPanel (タイトル / slug / ステータス / パスワード / 自動返信文面)
 *   - 中央: PageOutline (セクションリスト + DnD)
 *   - 右: PreviewCanvas (ライブプレビュー)
 *   - SectionEditorSheet: 右からスライドインする section 編集 sheet
 */
import { ArrowLeft, ExternalLink, Globe, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useDeleteDocument,
  useDocument,
  useUpsertDocument,
} from '@/hooks/useDocuments';
import { paths } from '@/routes/paths';

import { DocumentBriefPanel } from './components/DocumentBriefPanel';
import { DocumentPageOutline } from './components/DocumentPageOutline';
import { DocumentPreviewCanvas } from './components/DocumentPreviewCanvas';
import { DocumentSectionEditorSheet } from './components/DocumentSectionEditorSheet';

export function DocumentEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: document, isLoading } = useDocument(id);
  const upsert = useUpsertDocument();
  const del = useDeleteDocument();

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [viewport, setViewport] = useState<'desktop' | 'tablet' | 'mobile'>(
    'desktop',
  );

  useEffect(() => {
    if (
      document &&
      !activeSectionId &&
      (document.sections?.length ?? 0) > 0
    ) {
      setActiveSectionId(document.sections[0].id);
    }
  }, [document, activeSectionId]);

  const handlePublishToggle = async () => {
    if (!document || !id) return;
    const next = document.status === 'published' ? 'draft' : 'published';
    if (
      next === 'draft' &&
      !confirm('公開を取り消して下書きに戻しますか？')
    )
      return;
    try {
      await upsert.mutateAsync({
        id,
        title: document.title,
        slug: document.slug,
        status: next,
        sections: document.sections ?? [],
        autoReplyTemplate: document.autoReplyTemplate ?? null,
      });
      toast.success(
        next === 'published' ? '公開しました' : '公開を取り消しました',
      );
    } catch (e) {
      toast.error(`変更失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleDelete = async () => {
    if (!document || !id) return;
    if (!confirm(`資料「${document.title}」を削除します。よろしいですか？`)) return;
    try {
      await del.mutateAsync(id);
      toast.success('削除しました');
      navigate(paths.documents);
    } catch (e) {
      toast.error(`削除失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  if (isLoading || !document || !id) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  const isPublished = document.status === 'published';
  const publicUrl = document.slug ? `/docs/${document.slug}` : '';

  return (
    <div className="flex flex-col h-screen bg-[var(--bg-root)]">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)] bg-[var(--bg-elevated)]">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(paths.documents)}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <h1 className="text-sm font-semibold truncate">{document.title}</h1>
          <span className="text-xs text-[var(--text-muted)] whitespace-nowrap">
            {isPublished ? `· /${document.slug}` : '· 下書き'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ViewportToggle value={viewport} onChange={setViewport} />
          {isPublished && document.slug && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[var(--accent)] hover:underline px-2"
            >
              <ExternalLink className="size-3" /> 公開ページ
            </a>
          )}
          <Button
            size="sm"
            onClick={handlePublishToggle}
            disabled={upsert.isPending}
          >
            {upsert.isPending ? (
              <Loader2 className="size-3.5 mr-1 animate-spin" />
            ) : (
              <Globe className="size-3.5 mr-1" />
            )}
            {isPublished ? '公開停止' : '公開'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={del.isPending}
            title="この資料を削除"
            className="text-[var(--text-muted)] hover:text-red-500"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-[340px] border-r border-[var(--border)] overflow-y-auto bg-[var(--bg-elevated)]">
          <DocumentBriefPanel document={document} />
        </div>

        <div className="w-[400px] border-r border-[var(--border)] overflow-y-auto bg-[var(--bg-root)]">
          <DocumentPageOutline
            document={document}
            activeSectionId={activeSectionId}
            onSelect={setActiveSectionId}
            onEdit={setEditingSectionId}
          />
        </div>

        <div className="flex-1 overflow-y-auto bg-[var(--bg-root)]">
          <DocumentPreviewCanvas
            document={document}
            activeSectionId={activeSectionId}
            viewport={viewport}
            onSelectSection={setActiveSectionId}
          />
        </div>
      </div>

      {editingSectionId && (
        <DocumentSectionEditorSheet
          document={document}
          sectionId={editingSectionId}
          onClose={() => setEditingSectionId(null)}
        />
      )}
    </div>
  );
}

function ViewportToggle({
  value,
  onChange,
}: {
  value: 'desktop' | 'tablet' | 'mobile';
  onChange: (v: 'desktop' | 'tablet' | 'mobile') => void;
}) {
  const items: Array<['desktop' | 'tablet' | 'mobile', string]> = [
    ['desktop', '1280'],
    ['tablet', '768'],
    ['mobile', '375'],
  ];
  return (
    <div className="flex border border-[var(--border)] rounded-md overflow-hidden">
      {items.map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={`px-2.5 py-1 text-[11px] ${
            value === k
              ? 'bg-[var(--accent)] text-white'
              : 'bg-transparent text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
